import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { Link, useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import toast from 'react-hot-toast';

// Import new architecture components
import { useDashboardContext } from '../../contexts/DashboardContext';
import { LoadingSpinner, DashboardStatsSkeleton, DataTableSkeleton, SearchFormSkeleton } from '../../components/ui/LoadingStates';
import ErrorBoundary, { ErrorAlert, useErrorHandler } from '../../components/ui/ErrorBoundary';
import MedicationNotification from './MedicationNotification';
import config from '../../config/config';
import propertise from '../../config/properties';

// Import CSS
import '../componentCss/webform.css';
import '../componentCss/sidebar.css';
import 'react-toastify/dist/ReactToastify.css';

const ImprovedStaffDashboard = () => {
  const navigate = useNavigate();
  const handleError = useErrorHandler();
  
  // Local state for medication notifications
  const [showMedicationDialog, setShowMedicationDialog] = useState(false);
  const [medicationData, setMedicationData] = useState(null);
  const [isLoadingMedication, setIsLoadingMedication] = useState(false);
  
  // Date range state for patient record dashboard
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  const [patientRecordStats, setPatientRecordStats] = useState({
    totalPatient: 0,
    dischargePatient: 0,
    activePatient: 0
  });
  
  // Get dashboard context
  const {
    // Data
    statistics,
    formData,
    totalRows,
    userRole,
    daysSinceLastUpdate,
    
    // Search functionality
    patientSearch,
    
    // Loading states
    isAnyLoading,
    
    // Error states
    hasAnyError,
    
    // Actions
    actions
  } = useDashboardContext();

  // Initialize staff dashboard data on mount
  useEffect(() => {
    const initializeStaffDashboard = async () => {
      try {
        await actions.refreshStaffDashboard();
      } catch (error) {
        handleError(error, 'Staff dashboard initialization');
      }
    };

    initializeStaffDashboard();
  }, [actions, handleError]);

  // Handle date range change
  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-fetch data when both dates are provided
    const updatedRange = { ...dateRange, [name]: value };
    if (updatedRange.from && updatedRange.to) {
      fetchPatientRecordStats(updatedRange.from, updatedRange.to);
    }
  };

  // Fetch patient record statistics
  const fetchPatientRecordStats = async (fromDate, toDate) => {
    try {
      const result = await actions.fetchPatientRecordDashboard?.(fromDate, toDate);
      if (result?.success) {
        setPatientRecordStats(result.data);
      } else {
        throw new Error(result?.error || 'Failed to fetch patient record statistics');
      }
    } catch (error) {
      handleError(error, 'Patient record statistics');
      // Set default values on error
      setPatientRecordStats({
        totalPatient: 0,
        dischargePatient: 0,
        activePatient: 0
      });
    }
  };

  // Handle search form submission
  const handleSearch = async () => {
    try {
      const result = await actions.searchPatients();
      if (!result?.success) {
        throw new Error(result?.error || 'Search failed');
      }
      // Success feedback is handled by the search hook
    } catch (error) {
      handleError(error, 'Patient search');
      toast.error('Search failed. Please check your criteria and try again.');
    }
  };

  // Handle search reset
  const handleReset = async () => {
    try {
      const result = await actions.resetPatientSearch();
      if (!result?.success) {
        throw new Error(result?.error || 'Reset failed');
      }
      toast.success('Search criteria cleared successfully');
    } catch (error) {
      handleError(error, 'Search reset');
      toast.error('Failed to reset search. Please refresh the page.');
    }
  };

  // Handle patient row click
  const handleRowClick = (patient) => {
    try {
      const recordId = patient.recordId || patient.id;
      const mrnNumber = patient.mrnNumber;
      
      if (!recordId) {
        throw new Error('Patient record ID not found');
      }
      
      if (!mrnNumber) {
        throw new Error('Patient MRN number not found');
      }
      
      // Store in localStorage as per original implementation
      localStorage.setItem('recordId', recordId);
      localStorage.setItem('patientMrnNumber', mrnNumber);
      
      // Navigate to patient record
      navigate(`${config.baseUrl}viewRecord/${propertise.patientAdmit}/${recordId}/Patient/isPatiendSearch`);
    } catch (error) {
      handleError(error, 'Patient record navigation');
      toast.error('Unable to open patient record. Please try again.');
    }
  };

  // Handle medication notification click
  const handleMedicationNotification = async (patient) => {
    try {
      setIsLoadingMedication(true);
      const result = await actions.fetchMedicationDetails(patient.mrnNumber);
      
      if (result?.success) {
        setMedicationData(result.data);
        setShowMedicationDialog(true);
      } else {
        throw new Error(result?.error || 'Failed to fetch medication details');
      }
    } catch (error) {
      handleError(error, 'Medication details');
      // Show user-friendly message
      toast.error('Unable to load medication details. Please try again.');
    } finally {
      setIsLoadingMedication(false);
    }
  };

  // Close medication dialog
  const closeMedicationDialog = () => {
    setShowMedicationDialog(false);
    setMedicationData(null);
  };

  // Memoized table columns with staff-specific features
  const columns = useMemo(() => [
    {
      name: '#',
      cell: (row, index) => index + 1,
      width: '60px',
      sortable: false
    },
    {
      name: 'Notification',
      cell: row => (
        row.hasNotification ? (
          <Button
            variant="link"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleMedicationNotification(row);
            }}
            disabled={isLoadingMedication}
            className="p-0"
          >
            <span className="blinking-icon" style={{ fontSize: '1.2rem' }}>
              {isLoadingMedication ? (
                <i className="fas fa-clock"></i>
              ) : (
                <i className="fas fa-bell"></i>
              )}
            </span>
          </Button>
        ) : null
      ),
      width: '80px',
      center: true,
      ignoreRowClick: true
    },
    {
      name: "EMR Number",
      selector: row => (row.emrNumber === 'NOT_REGISTERED' ? 'N/A' : row.emrNumber) || 'N/A',
      sortable: true,
      wrap: true
    },
    {
      name: "MRN Number",
      selector: row => row.mrnNumber || 'N/A',
      sortable: true,
      wrap: true
    },
    {
      name: "Name",
      selector: row => row.patientName || `${row.firstName || 'N/A'} ${row.lastName || ''}`.trim(),
      sortable: true,
      wrap: true
    },
    {
      name: "Mobile Number",
      selector: row => row.mobileNumber || 'N/A',
      sortable: true,
      wrap: true
    },
    {
      name: "DOB",
      selector: row => row.dateOfBirth || 'N/A',
      sortable: true,
      wrap: true
    },
    {
      name: "Status",
      cell: row => (
        <span style={{ color: row.status === 'active' ? 'green' : 'red' }}>
          {row.status === 'active' ? 'Active' : 'Discharged'}
        </span>
      ),
      sortable: true,
      selector: row => row.status,
    },
    {
      name: "Current Location",
      selector: row => row.location || 'N/A',
      sortable: true,
      wrap: true
    }
  ], [isLoadingMedication]);

  // Memoized statistics cards for staff dashboard
  const statisticsCards = useMemo(() => [
    {
      title: 'No. of Admission',
      value: statistics.totalPatients || 0,
      icon: 'user-plus',
      color: '#007bff'
    },
    {
      title: 'No. of Discharged',
      value: statistics.dischargedPatients || 0,
      icon: 'user-minus',
      color: '#dc3545'
    },
    {
      title: 'No. of Active Patient',
      value: statistics.activePatients || 0,
      icon: 'user-check',
      color: '#28a745'
    }
  ], [statistics]);

  // Search form fields configuration
  const searchFields = [
    { name: 'FirstName', label: 'First Name', type: 'text' },
    { name: 'LastName', label: 'Last Name', type: 'text' },
    { name: 'MrnNumber', label: 'MRN Number', type: 'text' },
    { name: 'MobileNumber', label: 'Mobile Number', type: 'text' },
    { name: 'EmiratesIdNumber', label: 'Emirates ID Number', type: 'text' },
    { name: 'PassportNumber', label: 'Passport Number', type: 'text' }
  ];

  // Status options with default to Active
  const statusOptions = [
    { value: '', label: 'All' },
    { value: '1', label: 'Active' },
    { value: '2', label: 'Discharged' },
    { value: '3', label: 'Deceased' },
    { value: '4', label: 'Pre-admit' }
  ];

  // Custom row styles for different patient statuses
  const conditionalRowStyles = [
    {
      when: row => row.status === 1,
      style: {
        backgroundColor: '#ffffff'
      }
    },
    {
      when: row => row.location_status !== "Infacility" && row.status === 1,
      style: {
        backgroundColor: '#e3f2fd'
      }
    },
    {
      when: row => row.status === 2 || row.status === 0,
      style: {
        backgroundColor: '#fafafa',
        opacity: 0.8
      }
    }
  ];

  return (
    <ErrorBoundary component="ImprovedStaffDashboard">
      <div id="content">
        <Container fluid className="p-4">
          {/* Medication Notification Dialog */}
          {showMedicationDialog && (
            <MedicationNotification
              show={showMedicationDialog}
              medPassDrugAssignedList={medicationData}
              onClose={closeMedicationDialog}
            />
          )}

          {/* Header with Date Range */}
          <Row className="mb-4">
            <Col md={6} sm={6}>
              <h5 className="headtitle">Staff Dashboard</h5>
            </Col>
            <Col md={3} sm={3}>
              <Form.Group className="fieldbox h-auto px-2">
                <Form.Label>From</Form.Label>
                <Form.Control
                  type="date"
                  className="form-control"
                  name="from"
                  value={dateRange.from}
                  onChange={handleDateRangeChange}
                />
              </Form.Group>
            </Col>
            <Col md={3} sm={3}>
              <Form.Group className="fieldbox h-auto px-2">
                <Form.Label>To</Form.Label>
                <Form.Control
                  type="date"
                  className="form-control"
                  name="to"
                  min={dateRange.from}
                  value={dateRange.to}
                  onChange={handleDateRangeChange}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Error Display */}
          {hasAnyError && (
            <Row className="mb-4">
              <Col>
                <ErrorAlert
                  error={hasAnyError}
                  onRetry={() => actions.refreshStaffDashboard(true)}
                  onDismiss={() => actions.clearError()}
                />
              </Col>
            </Row>
          )}

          {/* Statistics Cards */}
          <Row className="text-center mb-4">
            {isAnyLoading ? (
              <DashboardStatsSkeleton count={3} />
            ) : (
              statisticsCards.map((card, index) => (
                <Col key={index} md={4} sm={6} className="mb-3">
                  <Card className="statistic-card h-100">
                    <Card.Body className="d-flex align-items-center justify-content-center">
                      <div className="text-center">
                        <div 
                          className="mb-2"
                          style={{ fontSize: '2rem', color: card.color }}
                        >
                          <i className={`fas fa-${card.icon}`}></i>
                        </div>
                        <Card.Title className="h6 mb-2">{card.title}</Card.Title>
                        <Card.Text className="h4 mb-0" style={{ color: card.color }}>
                          {card.value.toLocaleString()}
                        </Card.Text>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>

          {/* Password Update Notice */}
          {parseInt(daysSinceLastUpdate) >= 83 && (
            <Row className="mb-4">
              <Col>
                <Card className="password-update-message border-warning">
                  <Card.Body className="bg-warning bg-opacity-10">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <strong>🔒 Security Notice:</strong> It's been {daysSinceLastUpdate || 0} days since you last updated your password. For your security, we recommend updating your password.
                      </div>
                      <Link to={`${config.baseUrl}changePassword`}>
                        <Button variant="warning" size="sm">
                          Update Password
                        </Button>
                      </Link>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Patient Search Section */}
          <Row>
            <Col className="border p-3 m-2" style={{ boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)' }}>
              {isAnyLoading ? (
                <SearchFormSkeleton />
              ) : (
                <>
                  <Row>
                    <Col>
                      <div className="innerhead mb-4">
                        <h5 className="headtitle">Search Patient</h5>
                        {patientSearch.searchSummary.hasActiveFilters && (
                          <small className="text-muted">
                            {patientSearch.searchSummary.totalResults} result(s) found
                          </small>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* Search Form */}
                  <Row>
                    {searchFields.map((field, index) => (
                      <Col key={index} md={4} sm={6} className="mb-3">
                        <Form.Group className="fieldbox h-auto px-2">
                          <Form.Label>{field.label}</Form.Label>
                          <Form.Control
                            type={field.type}
                            placeholder={`Search By ${field.label}`}
                            className="form-control"
                            name={field.name}
                            value={patientSearch.searchTerms[field.name] || ''}
                            onChange={patientSearch.handleSearchChange}
                            isInvalid={!!patientSearch.searchError}
                          />
                        </Form.Group>
                      </Col>
                    ))}
                    
                    <Col md={12} sm={12} className="mb-3">
                      <Form.Group className="fieldbox h-auto px-2">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          className="form-control"
                          value={patientSearch.searchTerms.dropdownValue || '1'}
                          onChange={patientSearch.handleSearchChange}
                          name="dropdownValue"
                          aria-label="Patient Status"
                        >
                          {statusOptions.map((option, index) => (
                            <option key={index} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Search Error Display */}
                  {patientSearch.searchError && (
                    <Row className="mb-3">
                      <Col>
                        <div className="alert alert-danger" role="alert">
                          {patientSearch.searchError}
                        </div>
                      </Col>
                    </Row>
                  )}

                  {/* Search Buttons */}
                  <Row>
                    <Col className="d-flex gap-2">
                      <Button 
                        className="btn-secondary-btn3" 
                        onClick={handleSearch}
                        disabled={isAnyLoading}
                      >
                        {isAnyLoading ? (
                          <>
                            <LoadingSpinner size="sm" text="" />
                            <span className="ms-2">Searching...</span>
                          </>
                        ) : (
                          'Search'
                        )}
                      </Button>
                      <Button 
                        className="btn-secondary-btn3" 
                        variant="outline-secondary"
                        onClick={handleReset}
                        disabled={isAnyLoading}
                      >
                        Reset
                      </Button>
                    </Col>
                  </Row>

                  <hr />

                  {/* Patient Table */}
                  <Row>
                    <Col>
                      <div className="dashboardTbl">
                        {isAnyLoading ? (
                          <DataTableSkeleton rows={10} columns={9} />
                        ) : (
                          <DataTable
                            columns={columns}
                            data={patientSearch.filteredData}
                            pagination
                            paginationPerPage={10}
                            paginationRowsPerPageOptions={[10, 15, 25, 50]}
                            onRowClicked={handleRowClick}
                            highlightOnHover
                            pointerOnHover
                            responsive
                            conditionalRowStyles={conditionalRowStyles}
                            customStyles={{
                              headCells: {
                                style: {
                                  backgroundColor: '#f8f9fa',
                                  color: '#495057',
                                  fontWeight: '600',
                                  fontSize: '14px',
                                  borderBottom: '2px solid #dee2e6'
                                },
                              },
                              rows: {
                                style: {
                                  '&:hover': {
                                    backgroundColor: '#f8f9fa !important',
                                    cursor: 'pointer'
                                  }
                                }
                              }
                            }}
                            noDataComponent={
                              <div className="text-center py-4">
                                                      <div className="mb-3">
                        <i className="fas fa-search" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                      </div>
                                <h5 className="text-muted">No patients found</h5>
                                <p className="text-muted">
                                  {patientSearch.searchSummary.hasActiveFilters 
                                    ? 'Try adjusting your search criteria'
                                    : 'No patient records available'
                                  }
                                </p>
                              </div>
                            }
                          />
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* Legend for row colors */}
                  <Row className="mt-3">
                    <Col>
                      <div className="d-flex gap-3 flex-wrap">
                        <div className="d-flex align-items-center">
                          <div 
                            className="me-2" 
                            style={{ 
                              width: '20px', 
                              height: '20px', 
                              backgroundColor: '#ffffff',
                              border: '1px solid #dee2e6'
                            }}
                          ></div>
                          <small className="text-muted">In-facility</small>
                        </div>
                        <div className="d-flex align-items-center">
                          <div 
                            className="me-2" 
                            style={{ 
                              width: '20px', 
                              height: '20px', 
                              backgroundColor: '#e3f2fd',
                              border: '1px solid #dee2e6'
                            }}
                          ></div>
                          <small className="text-muted">Out of facility</small>
                        </div>
                        <div className="d-flex align-items-center">
                          <div 
                            className="me-2" 
                            style={{ 
                              width: '20px', 
                              height: '20px', 
                              backgroundColor: '#fafafa',
                              border: '1px solid #dee2e6'
                            }}
                          ></div>
                          <small className="text-muted">Discharged</small>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </ErrorBoundary>
  );
};

export default ImprovedStaffDashboard; 